import { useEffect, useRef, memo } from 'react';

/**
 * Componente de placeholder animado com efeito de digitação.
 * Usa React.memo com comparação profunda dos textos para evitar re-renders desnecessários.
 * Toda a animação é controlada internamente via refs — não causa re-render no componente pai.
 */
const AnimatedPlaceholderInner = ({ texts = [], typingSpeed = 40, pauseDuration = 2500, eraseDuration = 400 }) => {
  const spanRef = useRef(null);
  const animationRef = useRef(null);
  const textsRef = useRef(texts);

  // Atualiza a ref quando os textos mudam
  useEffect(() => {
    textsRef.current = texts;
  }, [texts]);

  useEffect(() => {
    const el = spanRef.current;
    if (!el || texts.length === 0) return;

    let cancelled = false;

    const sleep = (ms) => new Promise((resolve) => {
      animationRef.current = setTimeout(resolve, ms);
    });

    const animate = async () => {
      // Se só tem um texto estático, mostra e para
      if (texts.length === 1) {
        el.textContent = texts[0];
        return;
      }

      let index = 0;
      let startLength = 0;

      while (!cancelled) {
        const currentTexts = textsRef.current;
        const text = currentTexts[index % currentTexts.length];

        // Digitar
        for (let i = startLength; i <= text.length; i++) {
          if (cancelled) return;
          el.textContent = text.slice(0, i);
          await sleep(typingSpeed);
        }

        if (cancelled) return;

        // Pausa
        await sleep(pauseDuration);
        if (cancelled) return;

        const nextText = currentTexts[(index + 1) % currentTexts.length];
        let commonPrefixLength = 0;
        for (let i = 0; i < Math.min(text.length, nextText.length); i++) {
          if (text[i] === nextText[i]) {
            commonPrefixLength++;
          } else {
            break;
          }
        }

        // Não quebrar palavras: voltar até o último espaço se a divergência cortar uma palavra no meio
        const isBoundary = (char) => char === undefined || /[\s.,!?;:()[\]]/.test(char);
        
        if (
          commonPrefixLength > 0 &&
          !isBoundary(text[commonPrefixLength - 1]) &&
          (!isBoundary(text[commonPrefixLength]) || !isBoundary(nextText[commonPrefixLength]))
        ) {
          while (commonPrefixLength > 0 && !isBoundary(text[commonPrefixLength - 1])) {
            commonPrefixLength--;
          }
        }

        // Apagar apenas até o prefixo em comum (já ajustado para não quebrar palavras)
        for (let i = text.length; i >= commonPrefixLength; i--) {
          if (cancelled) return;
          el.textContent = text.slice(0, i);
          await sleep(typingSpeed / 2);
        }

        if (cancelled) return;

        // Pequena pausa antes do próximo
        await sleep(eraseDuration);

        startLength = commonPrefixLength;
        index = (index + 1) % currentTexts.length;
      }
    };

    animate();

    return () => {
      cancelled = true;
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, [texts, typingSpeed, pauseDuration, eraseDuration]);

  return <span ref={spanRef} style={{ display: 'inline' }} />;
};

// Comparação profunda dos textos para evitar re-renders quando as props são iguais
const areEqual = (prevProps, nextProps) => {
  if (prevProps.typingSpeed !== nextProps.typingSpeed) return false;
  if (prevProps.pauseDuration !== nextProps.pauseDuration) return false;
  if (prevProps.eraseDuration !== nextProps.eraseDuration) return false;

  const prevTexts = prevProps.texts || [];
  const nextTexts = nextProps.texts || [];

  if (prevTexts.length !== nextTexts.length) return false;
  for (let i = 0; i < prevTexts.length; i++) {
    if (prevTexts[i] !== nextTexts[i]) return false;
  }

  return true;
};

export const AnimatedPlaceholder = memo(AnimatedPlaceholderInner, areEqual);
